import { TreeCard } from './TreeCard';
import type { TreeProperties } from '../lib/types';

interface Props {
  properties: TreeProperties;
  selected: boolean;
  onToggleSelect(): void;
  onClose(): void;
}

/**
 * The tree card on a touchscreen.
 *
 * Without this, everything the app knows about an individual tree is
 * unreachable on a phone. The card was only ever rendered from `mousemove`,
 * which a touchscreen never fires -- so species, address, health and the "no
 * LiDAR canopy" warning were desktop-only, and tapping a tree the survey missed
 * did nothing at all with no explanation.
 *
 * A tap still selects, exactly as a click does; this card is additional rather
 * than a step in front of it. The button restates that selection so it can be
 * undone here, instead of by finding a circle under your thumb again.
 */
export function TreeInspector({ properties, selected, onToggleSelect, onClose }: Props) {
  const noCanopy = properties.has_canopy === 0;

  return (
    <div
      className={`tip tip--pinned${noCanopy ? ' tip--warning' : ''}`}
      role="dialog"
      aria-label="Tree details"
    >
      <button type="button" className="tip__close" onClick={onClose} aria-label="Close">
        ×
      </button>

      <TreeCard properties={properties}>
        {!noCanopy && (
          <button type="button" className="tip__action" onClick={onToggleSelect}>
            {selected ? 'Deselect this tree' : 'Isolate this tree'}
          </button>
        )}
      </TreeCard>
    </div>
  );
}
